/**
 * FAQCategoryEditor.tsx — FAQ 分类新建弹窗（#221 faq_categories 可配置注册表）
 *
 * FAQ 分类从旧 feedback.category 闭集升级为可配置注册表（key 稳定 + title_i18n 多语言 + 排序 + 上下架）。
 * 本弹窗覆盖「新建分类」：key（UPPER_SNAKE，创建后不可改）+ title_i18n（en 必填非空）→ **建即启用**
 * （createFaqCategory POST → 成功后 updateFaqCategory PATCH is_active=true，create-then-activate）。
 * 无「保存草稿」——草稿分类（inactive）无管理 UI 启用、不进 FAQ 下拉、重试撞 key，是死路（codex #235 P2）。
 * 成功 → invalidate ['faq-categories'] + ['faqs']（mutation 内）。分类编辑 / 删除 UI 见 docs/plans（NOT-in-scope）。
 *
 * 视觉层复用 ops 域弹窗惯例（~/ui/Dialog + FormRow + MultiLangFormRows），与 FAQEditor 同构。
 */

import type { CreateFaqCategoryRequest } from '@novel-admin/shared';
import { useState } from 'react';
import { useSysConfig } from '~/data/queries';
import { Dialog } from '~/ui/Dialog';
import { type FormRowProps, FormRow as UIFormRow } from '~/ui/FormRow';
import { MultiLangFormRows } from '~/ui/MultiLangFormRows';
import { type OpsMutations, errorToast, successToast } from '../use-ops-mutations';

const FormRow = (p: FormRowProps) => (
  <UIFormRow labelWidth={140} gap={14} pad="12px 0" labelPadTop={6} {...p} />
);

/** 分类 key UPPER_SNAKE（对齐 canonical faq_categories.key CHECK `^[A-Z][A-Z0-9_]*$`）*/
const KEY_RE = /^[A-Z][A-Z0-9_]*$/;

/** 剔除 trim 后为空的可选 locale（必填 en 已由 formValid 守住），避免空串污染 title_i18n。 */
function pruneBlankLocales(m: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(m).filter(([, v]) => v.trim() !== ''));
}

export function FAQCategoryEditor({
  mutations,
  canUpsert,
  onClose,
}: {
  mutations: OpsMutations;
  /** 写权（root/admin/operator）；viewer=false → save 禁用 */
  canUpsert: boolean;
  onClose: () => void;
}) {
  const { data: sys } = useSysConfig();
  const enabledLangs = sys.languages.filter((l) => l.enabled).map((l) => l.language_code);
  const [key, setKey] = useState('');
  const [title, setTitle] = useState<Record<string, string>>({ zh: '', en: '' });

  const keyValid = KEY_RE.test(key);
  const titleValid = !!title.en?.trim();
  const formValid = keyValid && titleValid;
  const busy = mutations.createFaqCategory.isPending || mutations.updateFaqCategory.isPending;

  const submit = () => {
    if (!canUpsert || !formValid) return;
    // 分类**必须建即启用**（create-then-activate）：去掉「保存草稿」死路——无分类管理 UI 启用 inactive 分类、
    // FAQ 下拉又过滤 inactive、重试新建同 key 撞 RESOURCE_CONFLICT，草稿分类将无从使用。
    // POST 不带 is_active（服务端硬编码 false，§10.2）→ create 成功后立即 PATCH is_active=true 启用。
    const body: CreateFaqCategoryRequest = {
      key,
      title_i18n: pruneBlankLocales(title),
    };
    mutations.createFaqCategory.mutate(body, {
      onSuccess: (created) => {
        mutations.updateFaqCategory.mutate(
          { id: created.id, body: { is_active: true } },
          {
            onSuccess: () => {
              successToast('已新建并启用 FAQ 分类');
              onClose();
            },
            // 已建、启用失败（rare）：草稿分类已存在（重试新建会撞 key）→ 关闭并提示；分类暂为下架态、不进 FAQ 下拉
            onError: (err) => {
              errorToast(err, '分类已创建但启用失败（草稿态，暂不可选）');
              onClose();
            },
          },
        );
      },
      onError: (err) => errorToast(err, '新建分类失败'),
    });
  };

  const submitTitle = !canUpsert
    ? '权限不足 · 该操作受角色限制（后端 requireRole 为权威）'
    : !formValid
      ? !keyValid
        ? 'key 须为 UPPER_SNAKE（大写字母/数字/下划线，不以数字开头）'
        : '分类名英文必填'
      : undefined;

  return (
    <Dialog
      width={620}
      title="新建 FAQ 分类"
      subtitle="key 稳定标识 · 创建后不可改 · 分类名英文必填 · 建即启用（随即可用于 FAQ 分类）"
      icon="IconFolder"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!canUpsert || !formValid || busy}
            title={submitTitle}
            onClick={submit}
          >
            新建并启用
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow label="分类 key" required hint="UPPER_SNAKE · 上线后不可改">
          <input
            className="input mono"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="如：REFUND_POLICY"
            style={{ maxWidth: 280 }}
          />
        </FormRow>

        <div style={{ padding: '16px 0 4px', borderBottom: '1px dashed var(--border)' }}>
          <span style={{ fontWeight: 500, fontSize: 12.5 }}>分类名</span>
          <span style={{ fontSize: 11, color: 'var(--fg-muted)', marginLeft: 6 }}>
            · 客户端 FAQ 分组标题
          </span>
        </div>
        <MultiLangFormRows
          label="分类名"
          value={title}
          onChange={setTitle}
          requiredLangs={['en']}
          availableLangs={enabledLangs}
          placeholderMap={{ zh: '充值 / 退款', en: 'Payment / Refund' }}
          placeholderTpl={(zh) => `${zh}分类名…`}
        />
      </div>
    </Dialog>
  );
}
